import { create } from "zustand"

import type {
  ChangeLog,
  ChangeLogCategory,
  ChangeLogListQuery,
  ChangeLogSortField,
  ChangeLogSubCategory,
  SortingOrder,
} from "@/graphql/codegen/graphql"
import { ChangeLogListDocument } from "@/graphql/codegen/graphql"
import { type SearchFilters, createSearchStore } from "@/stores/createSearchStore"

export type ChangeLogSearchFilters = SearchFilters & {
  field?: ChangeLogSortField
  order?: SortingOrder

  categories?: ChangeLogCategory[]
  subcategories?: ChangeLogSubCategory[]
  userIds?: string[]
  start?: string
  end?: string
}

const deduplicateData = (newData: ChangeLog[], existingData: ChangeLog[]): ChangeLog[] => {
  const uniqueIds = new Set(existingData.map((changeLogItem) => changeLogItem.changeLogId))
  return [...existingData, ...newData.filter((changeLogItem) => !uniqueIds.has(changeLogItem.changeLogId))]
}

export const useChangeLogSearchStore = create(
  createSearchStore<ChangeLog, ChangeLogSearchFilters, ChangeLogListQuery>({
    documentNode: ChangeLogListDocument,
    deduplicateData,
    getQueryInput: (filters: ChangeLogSearchFilters, offset: number, limit: number) => {
      const { field, order, start, end, ...otherFilters } = filters
      return {
        offset,
        limit,
        filter: {
          ...otherFilters,
          ...(start && {
            dateRange: {
              start,
              end,
            },
          }),
        },
        sort: { field, order },
      }
    },
    getQueryVariables: (queryInput) => ({ input: queryInput }),
    extractDataFromResponse: (data) => ({
      __typename: data.changeLog.list.__typename,
      data:
        data?.changeLog.list.__typename === "ChangeLogListSuccess"
          ? (data.changeLog.list.changeLogs as ChangeLog[])
          : [],
      error: data?.changeLog.list.__typename === "ChangeLogListFailure" ? data.changeLog.list.error : undefined,
    }),
  })
)
